import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { API_BASE } from '../../config/api';
import { periodLabel } from '../../lib/studyGuide';

const API = `${API_BASE}/api`;

const Section = ({ title, children }) => (
  <section className="space-y-2">
    <h3 className="text-sm font-semibold uppercase tracking-wider text-teal-400">{title}</h3>
    {children}
  </section>
);

const List = ({ items, ordered }) => {
  const Tag = ordered ? 'ol' : 'ul';
  return (
    <Tag className={`${ordered ? 'list-decimal' : 'list-disc'} pl-5 space-y-1 text-sm text-zinc-300`}>
      {(items || []).map((it, i) => <li key={i}>{it}</li>)}
    </Tag>
  );
};

export default function StudyGuideReviewDialog({ open, onOpenChange, programmeId, row, unit, token }) {
  const [content, setContent] = useState(null);

  useEffect(() => {
    if (!open || !row) return;
    setContent(null);
    axios.get(`${API}/programmes/${programmeId}/periods/${row.period_id}/study-guide`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setContent(r.data.content || {}))
      .catch(() => setContent({}));
  }, [open, row, programmeId, token]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="study-guide-review-dialog">
        <DialogHeader>
          <DialogTitle className="text-white">Study guide — {row ? periodLabel(unit, row.position, row.title) : ''}</DialogTitle>
          <DialogDescription className="text-zinc-400">{row?.title} — preview of the content in the Word document.</DialogDescription>
        </DialogHeader>
        {!content ? (
          <p className="text-zinc-500 text-sm">Loading…</p>
        ) : (
          <div className="space-y-6">
            {content.introduction && (
              <Section title="Introduction">
                {String(content.introduction).split('\n').filter(Boolean).map((p, i) => <p key={i} className="text-sm text-zinc-300 leading-relaxed">{p}</p>)}
              </Section>
            )}
            {content.learning_outcomes?.length > 0 && <Section title="Learning outcomes"><List items={content.learning_outcomes} ordered /></Section>}
            {(content.courses || []).map((c, ci) => (
              <Section key={ci} title={`Course ${ci + 1}`}>
                {c.summary && <p className="text-sm text-zinc-300">{c.summary}</p>}
                {(c.sections || []).map((s, si) => (
                  <div key={si} className="pl-3 border-l border-zinc-800">
                    <p className="text-white text-sm font-medium">{s.title}</p>
                    {s.summary && <p className="text-sm text-zinc-400 mt-1">{s.summary}</p>}
                    <List items={s.key_points} />
                  </div>
                ))}
              </Section>
            ))}
            {content.key_terms?.length > 0 && (
              <Section title="Key terms">
                <dl className="grid sm:grid-cols-[160px_1fr] gap-x-4 gap-y-2 text-sm">
                  {content.key_terms.map((t, i) => (
                    <React.Fragment key={i}><dt className="text-white font-medium">{t.term}</dt><dd className="text-zinc-400">{t.definition}</dd></React.Fragment>
                  ))}
                </dl>
              </Section>
            )}
            {content.lab_preparation?.length > 0 && <Section title="Preparing for your learning lab"><List items={content.lab_preparation} /></Section>}
            {content.reflection_questions?.length > 0 && <Section title="Reflection questions"><List items={content.reflection_questions} ordered /></Section>}
            {content.study_tips?.length > 0 && <Section title="Study tips"><List items={content.study_tips} /></Section>}
            <p className="text-xs text-zinc-500">The Word document also includes a cover page, this {unit.toLowerCase()}'s activity list, how it is assessed (from the rubric) and a notes page.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
